import React from 'react';
import { Alert, Box, Card, Grid, Group, NumberInput, Tabs, Text, ThemeIcon } from '@mantine/core';
import { IconCalculator, IconFileDescription, IconInfoCircle, IconReportMoney, IconTrendingUp } from '@tabler/icons-react';
import type { ICotizacionPostventa } from '../../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import { ResumenComercialPostventaVista } from '../resumenComercialPostventaVista';
import { DesgloseCostosPostventaVista } from '../desgloseCostosPostventaVista';
import type { CampoCotizacionPostventa } from '../../interfaces';

interface PasoPrecioVistaProps {
    cotizacion: ICotizacionPostventa;
    incoterms: { value: string; label: string }[];
    errores: Record<string, string>;
    requiereRecalculo: boolean;
    alCambiar: (campo: CampoCotizacionPostventa, valor: string | number) => void;
}

/** Paso 4: factor precio y costo de importación; resumen comercial y desglose de costos. */
export const PasoPrecioVista: React.FC<PasoPrecioVistaProps> = ({ cotizacion, incoterms, errores, requiereRecalculo, alCambiar }) => {
    const numero = (campo: CampoCotizacionPostventa) => (v: string | number) => alCambiar(campo, v === '' ? '' : Number(v));

    return (
        <Grid gutter="md">
            <Grid.Col span={12}>
                <Card shadow="sm" p="md" radius="md" withBorder>
                    <Group gap="xs" mb="sm">
                        <ThemeIcon variant="light" color="primary" radius="xl" size="md"><IconTrendingUp size={18} /></ThemeIcon>
                        <Text fw={600} size="md">Precio</Text>
                    </Group>
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <NumberInput size="xs" withAsterisk label="Factor precio" description="Toma el valor de configuración" min={0} allowNegative={false} hideControls decimalScale={3} value={cotizacion.FactorPrecio} onChange={numero('FactorPrecio')} error={errores.FactorPrecio} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                            <NumberInput size="xs" label="Costo importación origen hasta obra" description="Flete internacional, aduana y entrega" min={0} allowNegative={false} hideControls prefix="$ " suffix=" COP" thousandSeparator="." decimalSeparator="," value={cotizacion.CostoImportacion} onChange={numero('CostoImportacion')} />
                        </Grid.Col>
                    </Grid>
                </Card>
            </Grid.Col>

            <Grid.Col span={12}>
                {!cotizacion.Resultado ? (
                    <Box p="xl" ta="center">
                        <ThemeIcon size={64} radius="xl" color="gray" variant="light" mb="md"><IconCalculator size={32} /></ThemeIcon>
                        <Text c="dimmed">Presiona "Calcular Cotización" para ver el precio y el desglose de costos.</Text>
                    </Box>
                ) : (
                    <>
                        {requiereRecalculo && (
                            <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />} mb="md">
                                Hay cambios después del último cálculo. Recalcula la cotización para actualizar los valores.
                            </Alert>
                        )}
                        <Tabs defaultValue="comercial">
                            <Tabs.List mb="md">
                                <Tabs.Tab value="comercial" leftSection={<IconFileDescription size={14} />}>Resumen Comercial</Tabs.Tab>
                                <Tabs.Tab value="costos" leftSection={<IconReportMoney size={14} />}>Desglose de Costos</Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="comercial">
                                <ResumenComercialPostventaVista cotizacion={cotizacion} resultado={cotizacion.Resultado} incoterms={incoterms} />
                            </Tabs.Panel>
                            <Tabs.Panel value="costos">
                                <DesgloseCostosPostventaVista cotizacion={cotizacion} resultado={cotizacion.Resultado} />
                            </Tabs.Panel>
                        </Tabs>
                    </>
                )}
            </Grid.Col>
        </Grid>
    );
};
